
import { defineComponent } from "vue";
import { useApiStore } from "@/stores/apiStore";
import { useToastStore } from "@/stores/toastStore";
import { useSiteStore } from "@/stores/siteStore";
import moment from "moment-timezone";
import { FormatBytes } from "@/utils/Utilities";
import type { DatasetType } from "@/types/dataset";
export default defineComponent({
	name: "imdex-previously-uploaded-dialog",
	setup() {
		const api = useApiStore();
		const toast = useToastStore();
		const siteStore = useSiteStore();
		const formatBytes = FormatBytes;
		return { api, toast, moment, formatBytes, siteStore };
	},
	props: {
		multiple: {
			type: Boolean,
			default: false
		},
		stagedFiles: {
			type: Array,
			default: () => []
		},
		value: {
			type: Boolean,
			default: false
		},
		type: {
			type: String,
			default: ""
		}
	},
	data() {
		return {
			selectedRow: "",
			users: [],
			siteUsers: [],
			searchTerm: "",
			storageFiles: [],
			fileDateRange: "All",
			fileUploadedBy: "All",
			availableDateRanges: [
				{ text: this.$t("import.prevUploadDialog.today"), value: "Today" },
				{ text: this.$t("import.prevUploadDialog.thisWeek"), value: "This Week" },
				{ text: this.$t("import.prevUploadDialog.pastMonth"), value: "Past Month" },
				{ text: this.$t("import.prevUploadDialog.ytd"), value: "YTD" },
				{ text: this.$t("import.prevUploadDialog.all"), value: "All" }
			],
			previouslyUploadedTableData: {
				tableData: [],
				options: {
					columns: {
						selected: {
							maxWidth: "5%"
						}
					}
				},
				columns: ["selected", "name", "uploaded", "uploaded_by", "size"]
			},
			tableLoading: true
		};
	},

	computed: {
		selectedFiles(): any {
			if (this.multiple) {
				return this.previouslyUploadedTableData.tableData.filter((item: any) => {
					return item.selected;
				});
			} else {
				return this.previouslyUploadedTableData.tableData.filter((item: any) => {
					return item.fileId == this.selectedRow;
				});
			}
		},
		// eslint-disable-next-line
		searchUsers(): any {
			// eslint-disable-next-line
			return [
				...this.users
					.filter((item: any) => {
						return !this.siteUsers.some((siteUser: any) => siteUser.userId == item.id);
					})
					.map((item: any) => {
						return { text: item.name, value: item.id };
					})
			];
		},
		searchFileUsers(): any {
			const uploadedByUsers = this.storageFiles
				.map((file: any) => {
					return {
						text: file.uploaded_by,
						value: file.uploaded_by
					};
				})
				.filter((user, index, currentArr) => {
					return (
						index ===
						currentArr.findIndex((item) => {
							return item.value === user.value;
						})
					);
				});

			uploadedByUsers.push({
				text: "All",
				value: "All"
			});

			return uploadedByUsers;
		}
	},
	mounted() {
		this.getStorageFiles();
		this.getUsers();
	},
	methods: {
		confirmSelectedFiles() {
			this.$emit("change", this.selectedFiles);
		},
		async getUsers() {
			const { data } = await this.api.get("/user/read");
			this.users = data;
		},
		async filterFiles() {
			let filtered = this.storageFiles;

			if (this.searchTerm) {
				filtered = filtered.filter((file: any) => file.name.indexOf(this.searchTerm) != -1);
			}

			if (this.fileUploadedBy && this.fileUploadedBy !== "All") {
				filtered = filtered.filter((file: any) => {
					return file.uploaded_by === this.fileUploadedBy;
				});
			}

			if (this.fileDateRange) {
				filtered = filtered.filter((file: any) => {
					const uploadedTime = moment(file.uploaded).startOf("day");
					const now = moment().startOf("day");

					switch (this.fileDateRange) {
						case "Today":
							return uploadedTime.isSame(now, "day");
						case "This Week":
							return uploadedTime.isSameOrAfter(now.clone().subtract(1, "week"));
						case "Past Month":
							return uploadedTime.isSameOrAfter(now.clone().subtract(1, "month"));
						case "YTD":
							return uploadedTime.isSameOrAfter(now.clone().subtract(1, "year"));
						default:
							return true;
					}
				});
			}

			this.previouslyUploadedTableData.tableData = filtered;
		},
		async getStorageFiles() {
			let datasetType: DatasetType = null;
			// let drillholeSubtype = "";
			if (this.type == "drillhole") {
				datasetType = "Drillhole";
				// drillholeSubtype = this.type; // requires slightly work as we currently don't save this for file uploads, just the use on generation
				// Drillhole
			} else if (this.type == "point") {
				datasetType = "Points";
			} else if (this.type == "shape") {
				datasetType = "Surface";
			} else if (this.type == "blockmodel") {
				datasetType = "Blockmodel";
			}

			const route = `/upload-dataset/read?site=${
				(this.siteStore.activeSite as any).id
			}&datasetType=${datasetType}&excludeBlobOnly=true&status=Complete`;
			try {
				const { data } = await this.api.get(route);

				// eslint-disable-next-line
				const storageFiles = data.files.map((storageFile: any) => {
					const formattedFile: any = {};

					formattedFile.fileId = storageFile.id;
					formattedFile.name = storageFile.id ? storageFile.fileName : storageFile.name;
					formattedFile.size = storageFile.size;

					if (storageFile.id) {
						formattedFile.uploaded = moment(storageFile.createdAt);
						formattedFile.uploaded_by = storageFile.uploadedBy.name;
						formattedFile.source = "DB Entry";
						formattedFile.status = this.$t("uploadHelper.metadataAvailable");
					} else {
						formattedFile.uploaded = moment(new Date());
						formattedFile.uploaded_by = "N/A";
						formattedFile.source = "Blob";
						formattedFile.status = this.$t("uploadHelper.noMetadata");
					}
					formattedFile.selected = false;
					return formattedFile;
				});
				const stagedFileIds = this.stagedFiles.map((item: any) => {
					return item.fileId;
				});
				this.storageFiles = storageFiles.filter((item: any) => {
					return stagedFileIds.indexOf(item.fileId) == -1;
				});
				this.previouslyUploadedTableData.tableData = this.storageFiles;
				this.tableLoading = false;
			} catch (e) {
				this.toast.toast({
					type: "error",
					message: this.$t(`import.prevUploadDialog.toastErrorMsg`),
					title: this.$t(`import.prevUploadDialog.toastErrorTitle`),
					dismissable: false
				});
			}
		}
	},
	watch: {
		value(v: boolean) {
			if (v) {
				this.storageFiles = [];
				this.getStorageFiles();
			}
		},
		fileDateRange() {
			this.filterFiles();
		},
		searchTerm() {
			this.filterFiles();
		},
		fileUploadedBy() {
			this.filterFiles();
		}
	}
});
